import { useCallback, useContext } from 'react';
import Stack from '@mui/material/Stack';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import { NumInputBoxTwoCol } from '../../../component';
import { SelectionContext } from 'components/editor/GridDndEditor/SelectedBlockInfoProvider';
import { selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice';
import type { RootState } from 'components/editor/grid/reduxStore/Store';
import { useNewBlockStyleSettings } from 'components/editor/hooks/useNewBlockSettings';
import { normalizeRotation } from 'components/editor/GridDndEditor/helpers/rotation';

export function RotationSetting() {
  const { t } = useTranslation();
  const { [FeatureFlags.blockRotation]: isBlockRotationEnabled } = useFeatureFlag([FeatureFlags.blockRotation]);
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;
  const sectionId = useSelector((state: RootState) => selectSectionIdByBlockId(state, blockId));

  const { getBlockStyle, updateBlockStyleInUi, updateBlockStyleWithUndo, saveCurrentStateToRef } = useNewBlockStyleSettings(
    blockId,
    sectionId ?? ''
  );

  const { rotation = 0 } = getBlockStyle(blockId);

  const handleFocus = useCallback(() => {
    const current = getBlockStyle(blockId);
    saveCurrentStateToRef({ ...current, rotation: normalizeRotation(current.rotation) });
  }, [getBlockStyle, saveCurrentStateToRef, blockId]);

  if (!isBlockRotationEnabled) {
    return null;
  }

  return (
    <Stack rowGap="16px" width="100%">
      <NumInputBoxTwoCol
        title={t('document.grid.block.design.rotation')}
        inputValue={rotation}
        ariaLabel="°"
        limitedNum={359}
        disableNegativeNum
        onFocus={handleFocus}
        onChange={(value: number) => updateBlockStyleInUi({ rotation: normalizeRotation(value) })}
        onBlur={(value: number) => updateBlockStyleWithUndo({ rotation: normalizeRotation(value) })}
        testId="block-rotation"
      />
    </Stack>
  );
}
